import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import uniqBy from 'lodash/uniqBy';
import { Provider } from '@vgtv/api-client';

import { Button } from '../Button/Button';
import { sortAssetsByFlightTimes } from '../../shared/helpers/assetHelpers';
import { groupAssetsByDate } from '../../helpers/groupAssetsByDate';
import { LiveAssetsGroup } from '../LiveSchedule/LiveAssetsGroup/LiveAssetsGroup';
import { formatDateOfMonth } from '../../helpers/date';
import { Spinner } from '../Spinner/Spinner';
import svpApiClient from '../../services/svpApiClient';
import { ensureFetchDelay } from '../../helpers/fetch';
import { ABCategory, VGTVCategory } from '../../types';
import { config } from '../../config';
import { useAppDispatch } from '../../modules/hooks';
import { fetchLiveSvpAssets } from '../../modules/assets/actions';
import { useVendor } from '../VendorPovider/VendorPovider';

import { AssetsScheduleHeader } from './AssetsScheduleHeader/AssetsScheduleHeader';

import type { ChipItem } from '../ChipStack/ChipStack';
import type { Opaque } from '@vgtv/api-client/lib/utils';
import type { CnpTag, CnpTagId } from '@vgtv/api-client/lib/cnp_tag';
import type { Dayjs } from 'dayjs';
import type {
  SvpCategory,
  SvpCategoryId,
} from '@vgtv/api-client/lib/svp_category';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { ReactNode, RefObject, VFC } from 'react';

import * as styles from './AssetsSchedule.module.scss';

const ASSETS_LIMIT = 100;

export interface AssetsScheduleProps {
  categoryId?: SvpCategory['id'];
  tagId?: CnpTag['id'][];
  title: string;
  onCloseButtonClick?: () => void;
  scrollContainerRef?: RefObject<HTMLElement>;
}

type AssetsFlagType = 'previous' | 'initial';
type AssetsFlags = Record<AssetsFlagType, boolean>;

const stateHelper = (key: AssetsFlagType, value: boolean) => {
  return (prev: AssetsFlags): AssetsFlags => ({
    ...prev,
    [key]: value,
  });
};

const findChip = (chip: ChipItem, ids: string[]): ChipItem => {
  if (!ids.length) {
    return chip;
  }

  const nextChip = chip.items?.find((item) => item.id === ids[0]) as ChipItem;

  return findChip(nextChip, ids.slice(1));
};

const getScrollPosition = (scrollContainer?: RefObject<HTMLElement>) => {
  return scrollContainer?.current?.scrollTop ?? window.scrollY;
};

const categoryNameMappings = {
  [VGTVCategory.NEWS]: 'Nyheter',
  [VGTVCategory.SPORT]: 'Sport',
  [ABCategory.SPORT]: 'Sport',
} as const;

export const ROOT_CATEGORY_ID = 0 as Opaque<0, 'SvpCategory'>;

const LIVE_CATEGORIES =
  config.provider === Provider.VGTV
    ? [VGTVCategory.SPORT, VGTVCategory.NEWS]
    : [ABCategory.SPORT];

function getFinalCategoryId(filter: ChipItem | null, categoryId?: number) {
  return (filter?.type === 'category' ? Number(filter.id) : categoryId) as
    | SvpCategoryId
    | undefined;
}

function getFinalTagId(filter: ChipItem | null, tagId?: string) {
  return (filter?.type === 'tag' ? filter.id : tagId) as CnpTagId | undefined;
}

const AssetsSchedule: VFC<AssetsScheduleProps> = ({
  categoryId,
  tagId,
  title,
  onCloseButtonClick,
  scrollContainerRef,
}) => {
  const { t } = useTranslation();
  const { vendor } = useVendor();
  const dispatch = useAppDispatch();

  const scrollThresholdRef = useRef<HTMLDivElement>(null!);
  const headerContainerRef = useRef<HTMLDivElement>(null!);
  const firstGroupRef = useRef<HTMLDivElement>(null!);
  const startDateRef = useRef<Dayjs>();
  const selectedChipRef = useRef<ChipItem | null>(null);
  const [isCategoryStructureLoading, setIsCategoryStructureLoading] = useState(
    tagId === undefined
  );
  const [chips, setChips] = useState<ChipItem[]>([]);
  const [selection, setSelection] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [assets, setAssets] = useState<SvpAssetAdditional[]>([]);
  const [isLoadPreviousEnabled, setIsLoadPreviousEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState<AssetsFlags>({
    previous: false,
    initial: true,
  });
  const [assetsAvailability, setAssetsAvailability] = useState<AssetsFlags>({
    previous: true,
    initial: true,
  });

  const assetGroups = useMemo(() => groupAssetsByDate(assets), [assets]);

  const datesToMark = useMemo(
    () => [...new Set(assetGroups.map(([date]) => date))],
    [assetGroups]
  );

  const assetGroupRefs = useRef<HTMLDivElement[]>([]);

  const scrollTop = useCallback(
    (amountOfPixels = 0, smooth = true) => {
      const scrollContainer = scrollContainerRef?.current ?? window;

      scrollContainer.scroll({
        top: amountOfPixels,
        behavior: smooth ? 'smooth' : 'auto',
      });
    },
    [scrollContainerRef]
  );

  const loadAssets = useCallback(
    async ({
      startDate,
      endDate,
      kind,
    }: {
      startDate: Dayjs;
      endDate?: Dayjs;
      kind: AssetsFlagType;
    }) => {
      setIsLoading(stateHelper(kind, true));

      const assets = await dispatch(
        fetchLiveSvpAssets({
          limit: ASSETS_LIMIT,
          categoryId: getFinalCategoryId(selectedChipRef.current, categoryId),
          tagId: getFinalTagId(
            selectedChipRef.current,
            Array.isArray(tagId) ? tagId.join(',') : tagId
          ),
          startDate: startDate?.unix(),
          endDate: endDate?.unix(),
          vendor,
        })
      ).unwrap();

      if (kind === 'initial') {
        setAssets(sortAssetsByFlightTimes(assets));
      } else {
        setAssets((prevAssets) => {
          return sortAssetsByFlightTimes(
            uniqBy([...prevAssets, ...assets], 'id')
          );
        });
      }

      setIsLoading(stateHelper(kind, false));

      setAssetsAvailability(stateHelper(kind, assets.length > 0));
    },
    [dispatch, categoryId, tagId, vendor]
  );

  const loadPreviousAssets = useCallback(
    async (selectedStartDate?: Dayjs, disableScrollRestoration?: boolean) => {
      const endDate = startDateRef.current;
      const startDate =
        selectedStartDate ?? endDate!.startOf('month').subtract(1, 'month');

      const element = firstGroupRef.current!;

      const scrollPosition = getScrollPosition(scrollContainerRef);

      const scrollOffset = scrollPosition + element.getBoundingClientRect().top;

      await loadAssets({ startDate, endDate, kind: 'previous' });

      if (!disableScrollRestoration) {
        scrollTop(element.getBoundingClientRect().top - scrollOffset, false);
      }

      startDateRef.current = startDate;
    },
    [scrollContainerRef, loadAssets, scrollTop]
  );

  const handleLoadPreviousClick = useCallback(() => {
    setIsLoadPreviousEnabled(true);
  }, []);

  const handleSelectionChange = useCallback(
    async (value: string[]) => {
      setIsLoadPreviousEnabled(false);
      setAssetsAvailability(stateHelper('previous', true));
      setSelectedDate(undefined);
      startDateRef.current = dayjs().startOf('day');

      setSelection(value);

      selectedChipRef.current = value.length
        ? findChip(chips.find((chip) => chip.id === value[0])!, value.slice(1))
        : null;

      scrollTop(0, false);

      await loadAssets({
        startDate: startDateRef.current!,
        kind: 'initial',
      });
    },
    [chips, scrollTop, loadAssets]
  );

  const handleDateChange = useCallback(
    async (value: Date) => {
      setIsLoadPreviousEnabled(false);

      const selectedDay = dayjs(value).startOf('day');

      setSelectedDate(value);

      if (datesToMark.every((date) => dayjs(date) > selectedDay)) {
        scrollTop(0);

        await loadPreviousAssets(selectedDay, true);
      } else {
        const index = datesToMark.findIndex(
          (date) => dayjs(date) >= selectedDay
        );

        const element =
          index === 0 ? firstGroupRef.current! : assetGroupRefs.current[index]!;

        const rect = element.getBoundingClientRect();

        const scrollPosition = getScrollPosition(scrollContainerRef);

        scrollTop(
          scrollPosition + rect.top - headerContainerRef.current.clientHeight
        );
      }
    },
    [loadPreviousAssets, datesToMark, scrollContainerRef, scrollTop]
  );

  useEffect(() => {
    if (
      !isLoadPreviousEnabled ||
      !assetsAvailability.previous ||
      isLoading.previous
    ) {
      return;
    }

    const observer = new IntersectionObserver(async (entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        await loadPreviousAssets();
      }
    });

    observer.observe(scrollThresholdRef.current);

    return () => observer.disconnect();
  }, [
    isLoadPreviousEnabled,
    assetsAvailability.previous,
    isLoading.previous,
    loadPreviousAssets,
  ]);

  useEffect(() => {
    startDateRef.current = dayjs().startOf('day');

    loadAssets({
      startDate: startDateRef.current,
      kind: 'initial',
    }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tagId || categoryId === undefined) {
      return;
    }

    const controller = new AbortController();

    setIsCategoryStructureLoading(true);

    if (categoryId === ROOT_CATEGORY_ID) {
      ensureFetchDelay(
        Promise.all(
          LIVE_CATEGORIES.map((liveCategoryId) => {
            return svpApiClient.getLiveCategoryStructure(
              {
                categoryId: liveCategoryId,
                vendor,
                structure: {
                  id: String(liveCategoryId),
                  type: 'category',
                  label: categoryNameMappings[liveCategoryId],
                  items: [],
                },
              },
              {
                signal: controller.signal,
              }
            );
          })
        )
      )
        .then((structures) => {
          setChips(structures);
          setIsCategoryStructureLoading(false);
        })
        .catch((err) => {
          if (err.name === 'AbortError') {
            return;
          }

          console.error(err);
        });
    } else {
      ensureFetchDelay(
        svpApiClient.getLiveCategoryStructure(
          {
            categoryId: categoryId,
            vendor,
            structure: {
              id: String(categoryId),
              type: 'category',
              label: '',
              items: [],
            },
          },
          {
            signal: controller.signal,
          }
        )
      )
        .then((structure) => {
          setChips(structure.items);
          setIsCategoryStructureLoading(false);
        })
        .catch((err) => {
          if (err.name === 'AbortError') {
            return;
          }

          console.error(err);
        });
    }

    return () => controller.abort();
  }, [categoryId, tagId, vendor]);

  const renderIndicator = useCallback((): ReactNode => {
    if (!assetsAvailability.previous && !isLoading.previous) {
      return null;
    }

    if (isLoadPreviousEnabled || isLoading.previous) {
      return (
        <div
          ref={isLoadPreviousEnabled ? scrollThresholdRef : undefined}
          className={styles.loaderContainer}
        >
          <Spinner size="small" />
        </div>
      );
    }

    return (
      <div className={styles.buttonContainer}>
        <Button
          variant="contained"
          size="medium"
          onClick={handleLoadPreviousClick}
        >
          {t('Previous videos')}
        </Button>
      </div>
    );
  }, [
    assetsAvailability.previous,
    isLoading.previous,
    isLoadPreviousEnabled,
    handleLoadPreviousClick,
    t,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer} ref={headerContainerRef}>
        <AssetsScheduleHeader
          title={title}
          datesToMark={datesToMark}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          selection={selection}
          onSelectionChange={handleSelectionChange}
          chips={chips}
          isChipStackLoading={isCategoryStructureLoading}
          onCloseButtonClick={onCloseButtonClick}
        />
      </div>
      <div>
        {isLoading.initial ? (
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
        ) : (
          <>
            {renderIndicator()}
            {assetGroups.length === 0 && (
              <div ref={firstGroupRef}>
                <LiveAssetsGroup />
              </div>
            )}
            {assetGroups.map(([date, assetsChunk], index) => (
              <div
                key={date}
                ref={
                  index === 0
                    ? firstGroupRef
                    : (ref) => {
                        assetGroupRefs.current[index] = ref!;
                      }
                }
              >
                <LiveAssetsGroup
                  categoryId={getFinalCategoryId(
                    selectedChipRef.current,
                    categoryId
                  )}
                  assets={assetsChunk}
                  title={formatDateOfMonth(date).toUpperCase()}
                />
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export { AssetsSchedule };
